/**
 * 角色卡管理页（T07 / T18）可见部件元数据。
 *
 * 本文件是「安卓移植清单」的数据源之一：页面里每个可以看到的部件都在这里声明
 * id / 角色 / 中文名 / 390x844 逻辑坐标 / zIndex / props / notes。
 *
 * 为什么 T18 才补这一份：`src/features/presets/**` 在 T07 回归修复后回到 `main`，
 * 但它的可见部件一直只在运行期经 `Framed.tsx` 的 `useComponentMeta()` 登记，
 * 静态产物 `public/component-manifest.json` 里没有 —— 安卓移植会缺件。
 * T18 按既有约定（社区页 / 提供商页 / 语音件）在这里声明式补齐，**只增不改**：
 * 不改 `PresetListView.tsx` / `PresetEditorView.tsx` 的运行时代码。
 *
 * 坐标约定：`layout.ts` 里的常量是**页面局部坐标**（y = 0 是内容区顶部），
 * `Framed.tsx` 在登记时统一 + `CONTENT_TOP`(24dp) 换算成 390x844 画布绝对坐标；
 * 本文件的 `canvas()` 做同一件事，因此清单坐标与页面运行期登记的坐标逐字段一致。
 *
 * 列表页 / 编辑页是同一个 page（`presets`），编辑态用更高的 zIndex 覆盖列表态。
 */
import type { ComponentMeta } from '../../core/types';
import type { BoxLayout, ComponentMetaInput } from '../../ui';
import { presetItemBoxes } from './listItemBoxes';
import { automationEditorMeta, roleVoiceAutoDesignMeta } from './automationLayout';
import { ROLE_MODEL_META } from './roleModelLayout';
import { intimacyControlMeta } from '../intimacy/layout';
import {
  ADD_ENTRY_BUTTON_BOX,
  CONTENT_TOP,
  CREATE_BUTTON_BOX,
  EDITOR_BACK_BOX,
  EDITOR_ERROR_BOX,
  EDITOR_HEADER_BOX,
  EDITOR_NOTICE_BOX,
  EDITOR_SAVE_BOX,
  EDITOR_TITLE_BOX,
  FIELD_AVATAR_LABEL_BOX,
  FIELD_DESC_BOX,
  FIELD_DESC_LABEL_BOX,
  FIELD_NAME_BOX,
  FIELD_NAME_LABEL_BOX,
  FIELD_PROMPT_BOX,
  FIELD_PROMPT_LABEL_BOX,
  FIELD_TAGS_BOX,
  FIELD_TAGS_LABEL_BOX,
  FORM_SCROLL_BOX,
  MODEL_CONFIG_BUTTON_BOX,
  IMPORT_CARD_BUTTON_BOX,
  IMPORT_WORLD_BOOK_BUTTON_BOX,
  IMPORT_WORLD_BOOK_EDITOR_BOX,
  LIST_BOX,
  LIST_HEADER_BOX,
  LIST_TITLE_BOX,
  PAGE_BOX,
  WORLD_BOOK_ENTRY_HEIGHT,
  WORLD_BOOK_ENTRY_STEP,
  WORLD_BOOK_ENTRY_TOP,
  WORLD_BOOK_TITLE_BOX,
} from './layout';

/** 本页在组件清单里的 page 标识（与运行期 `page: 'presets'` 一致）。 */
export const PAGE_ID = 'presets';

interface MetaOptions {
  zIndex?: number;
  props?: string[];
  notes?: string;
}

/** 页面局部坐标 → 390x844 画布绝对坐标（与 `Framed.tsx` 的 `toCanvasMeta` 同源）。 */
function canvas(layout: BoxLayout): BoxLayout {
  return { ...layout, y: layout.y + CONTENT_TOP };
}

function meta(
  id: string,
  label: string,
  role: NonNullable<ComponentMetaInput['role']>,
  layout: BoxLayout,
  options: MetaOptions = {},
): ComponentMetaInput {
  return {
    id,
    page: PAGE_ID,
    label,
    role,
    // layout 一律传页面局部坐标，这里统一换算成画布坐标，避免两处各写一套。
    layout: canvas(layout),
    zIndex: options.zIndex ?? 0,
    ...(options.props === undefined ? {} : { props: options.props }),
    notes: options.notes ?? `${label}；安卓用 Fragment/约束布局容器`,
  };
}

/* -------------------------------------------------------------------------- *
 * 列表页：视图私有几何的唯一声明（`PresetListView.tsx` 通过 `presetLocalLayout`
 * 读取，不再在视图里内联一份坐标）。
 * -------------------------------------------------------------------------- */

const LIST_EMPTY_BOX: BoxLayout = { x: 40, y: 300, width: 310, height: 24 };
const LIST_NOTICE_BOX: BoxLayout = { x: 16, y: 596, width: 358, height: 44 };

const DELETE_BACKDROP_BOX: BoxLayout = { x: 0, y: 0, width: 390, height: 744 };
const DELETE_PANEL_BOX: BoxLayout = { x: 20, y: 280, width: 350, height: 200 };
const DELETE_MESSAGE_BOX: BoxLayout = { x: 36, y: 300, width: 318, height: 60 };
const DELETE_CANCEL_BOX: BoxLayout = { x: 36, y: 400, width: 150, height: 44 };
const DELETE_CONFIRM_BOX: BoxLayout = { x: 204, y: 400, width: 150, height: 44 };

/* -------------------------------------------------------------------------- *
 * 编辑页：世界书条目模板几何
 * -------------------------------------------------------------------------- *
 * `PresetEditorView.tsx` 通过 `worldBookEntryBoxes()` 读取，不再在视图里内联一份
 * 条目几何；本函数是视图定位与清单登记共用的唯一来源。
 */

export interface WorldBookEntryBoxes {
  card: BoxLayout;
  keywordsLabel: BoxLayout;
  keywordsInput: BoxLayout;
  constant: BoxLayout;
  rules: BoxLayout;
  contentLabel: BoxLayout;
  contentInput: BoxLayout;
  enabled: BoxLayout;
  priorityLabel: BoxLayout;
  priorityInput: BoxLayout;
  remove: BoxLayout;
}

/** 第 index 条世界书条目的几何（页面局部坐标）。 */
export function worldBookEntryBoxes(index: number, offset = 0): WorldBookEntryBoxes {
  const top = WORLD_BOOK_ENTRY_TOP + offset + index * WORLD_BOOK_ENTRY_STEP;
  return {
    card: { x: 8, y: top, width: 374, height: WORLD_BOOK_ENTRY_HEIGHT },
    keywordsLabel: { x: 20, y: top + 10, width: 200, height: 16 },
    keywordsInput: { x: 20, y: top + 28, width: 350, height: 36 },
    constant: { x: 260, y: top + 2, width: 110, height: 24 },
    rules: { x: 20, y: top + 208, width: 350, height: 48 },
    contentLabel: { x: 20, y: top + 72, width: 200, height: 16 },
    contentInput: { x: 20, y: top + 90, width: 350, height: 64 },
    enabled: { x: 20, y: top + 166, width: 96, height: 32 },
    priorityLabel: { x: 140, y: top + 172, width: 64, height: 16 },
    priorityInput: { x: 208, y: top + 166, width: 80, height: 32 },
    remove: { x: 298, y: top + 160, width: 72, height: 44 },
  };
}

/* -------------------------------------------------------------------------- *
 * 列表页静态部件
 * -------------------------------------------------------------------------- */

/** 第 0 个列表项的几何（模板行；运行时按角色卡 id 逐项登记）。 */
const ITEM_TEMPLATE = presetItemBoxes(0);

const LIST_COMPONENTS: ComponentMetaInput[] = [
  meta('presets.root', '联系人页面容器', 'container', PAGE_BOX, {
    zIndex: 2,
    notes: '列表视图根容器（坐标含 24dp 状态栏偏移）；安卓用 Fragment 根 View',
  }),
  meta('presets.header', '联系人标题栏', 'container', LIST_HEADER_BOX, {
    zIndex: 5,
    notes: '标题 + 导入 / 新建按钮容器；安卓用 MaterialToolbar',
  }),
  meta('presets.header.title', '联系人', 'text', LIST_TITLE_BOX, {
    zIndex: 6,
    notes: '页面主标题；安卓用 MaterialToolbar 标题（TextAppearance.Headline）',
  }),
  meta('presets.importCardButton', '导入角色卡', 'button', IMPORT_CARD_BUTTON_BOX, {
    zIndex: 6,
    props: ['pressed'],
    notes: '选择 JSON 文件导入角色卡；安卓用 MaterialButton + ACTION_OPEN_DOCUMENT（minHeight 44dp）',
  }),
  meta('presets.importWorldBookButton', '导入世界书', 'button', IMPORT_WORLD_BOOK_BUTTON_BOX, {
    zIndex: 6,
    props: ['pressed'],
    notes: '选择 JSON 文件导入世界书并生成角色卡；安卓用 MaterialButton + ACTION_OPEN_DOCUMENT（minHeight 44dp）',
  }),
  meta('presets.createButton', '新建', 'button', CREATE_BUTTON_BOX, {
    zIndex: 6,
    props: ['pressed'],
    notes: '新建角色卡；安卓用 ExtendedFloatingActionButton（或 Toolbar action）',
  }),
  meta('presets.list', '联系人列表', 'list', LIST_BOX, {
    zIndex: 4,
    props: ['items'],
    notes: '可滚动列表；安卓用 RecyclerView（垂直）；卡片高 146dp、步进 158dp',
  }),
  meta('presets.empty', '空状态提示', 'text', LIST_EMPTY_BOX, {
    zIndex: 4,
    notes: '没有角色卡时的引导文案；安卓用 TextView（居中、次要文本色）',
  }),
  meta('presets.item', '角色卡卡片（列表项模板）', 'container', ITEM_TEMPLATE.item, {
    zIndex: 4,
    props: ['selected'],
    notes: '列表项模板；运行时按角色卡 id 登记为 presets.item.<id>；安卓用 MaterialCardView（radius-md、shadow-1）',
  }),
  meta('presets.item.avatar', '卡片头像', 'icon', ITEM_TEMPLATE.avatar, {
    zIndex: 5,
    notes: '头像占位（图片 / emoji / 名称首字）；安卓用 ShapeableImageView（64dp 圆形）',
  }),
  meta('presets.item.name', '卡片名称', 'text', ITEM_TEMPLATE.name, {
    zIndex: 5,
    notes: '角色卡名称；安卓用 TextView（单行、ellipsize=end）',
  }),
  meta('presets.item.tags', '卡片标签', 'text', ITEM_TEMPLATE.tags, {
    zIndex: 5,
    notes: '标签 · 世界书条数概览；安卓用 TextView（单行）或 ChipGroup',
  }),
  meta('presets.item.description', '卡片简介', 'text', ITEM_TEMPLATE.description, {
    zIndex: 5,
    notes: '角色简介；安卓用 TextView（maxLines=2、ellipsize=end）',
  }),
  meta('presets.item.editButton', '编辑角色卡', 'button', ITEM_TEMPLATE.edit, {
    zIndex: 6,
    props: ['pressed'],
    notes: '进入角色卡编辑页；安卓用 MaterialButton（text 样式）',
  }),
  meta('presets.item.duplicateButton', '复制角色卡', 'button', ITEM_TEMPLATE.duplicate, {
    zIndex: 6,
    props: ['pressed'],
    notes: '复制一张角色卡并追加「副本」后缀；安卓用 MaterialButton（text 样式）',
  }),
  meta('presets.item.exportButton', '导出角色卡', 'button', ITEM_TEMPLATE.exportJson, {
    zIndex: 6,
    props: ['pressed'],
    notes: '导出单张角色卡 JSON；安卓用 MaterialButton + SAF CreateDocument',
  }),
  meta('presets.item.deleteButton', '删除角色卡', 'button', ITEM_TEMPLATE.remove, {
    zIndex: 6,
    props: ['pressed'],
    notes: '删除（先弹二次确认）；安卓用 MaterialButton（危险色）+ MaterialAlertDialog',
  }),
  meta('presets.notice', '导入 / 导出提示', 'text', LIST_NOTICE_BOX, {
    zIndex: 30,
    notes: '导入失败 / 导出结果的非阻断提示；安卓用 Snackbar（LENGTH_LONG）',
  }),
  meta('presets.deleteDialog.backdrop', '删除确认遮罩', 'background', DELETE_BACKDROP_BOX, {
    zIndex: 40,
    notes: '弹层遮罩（点击空白取消）；安卓用 MaterialAlertDialog 自带的 dim 层',
  }),
  meta('presets.deleteDialog', '删除确认弹窗', 'modal', DELETE_PANEL_BOX, {
    zIndex: 41,
    notes: '删除二次确认；安卓用 MaterialAlertDialog（radius-md、上滑 sheet 动效）',
  }),
  meta('presets.deleteDialog.message', '删除确认文案', 'text', DELETE_MESSAGE_BOX, {
    zIndex: 42,
    notes: '确认文案（含角色卡名）；安卓用 MaterialAlertDialog 的 message（WrapContent）',
  }),
  meta('presets.deleteDialog.cancelButton', '取消删除', 'button', DELETE_CANCEL_BOX, {
    zIndex: 42,
    props: ['pressed'],
    notes: '取消导入 / 删除；安卓用 MaterialAlertDialog negative button',
  }),
  meta('presets.deleteDialog.confirmButton', '确认删除', 'button', DELETE_CONFIRM_BOX, {
    zIndex: 42,
    props: ['pressed'],
    notes: '确认删除（写 presetRepo）；安卓用 MaterialAlertDialog positive button（危险色）',
  }),
];

/* -------------------------------------------------------------------------- *
 * 编辑页静态部件
 * -------------------------------------------------------------------------- */

const ENTRY_TEMPLATE = worldBookEntryBoxes(0);

const EDITOR_COMPONENTS: ComponentMetaInput[] = [
  meta('presets.editor.root', '联系人编辑页面容器', 'container', PAGE_BOX, {
    zIndex: 2,
    notes: '编辑视图根容器（坐标含 24dp 状态栏偏移）；安卓用 Fragment 根 View',
  }),
  meta('presets.editor.header', '编辑页标题栏', 'container', EDITOR_HEADER_BOX, {
    zIndex: 5,
    notes: '返回 + 标题 + 保存容器；安卓用 MaterialToolbar',
  }),
  meta('presets.editor.title', '编辑联系人', 'text', EDITOR_TITLE_BOX, {
    zIndex: 6,
    notes: '新建 / 编辑角色卡标题；安卓用 MaterialToolbar 标题',
  }),
  meta('presets.editor.backButton', '返回', 'button', EDITOR_BACK_BOX, {
    zIndex: 6,
    props: ['pressed'],
    notes: '放弃编辑并返回列表；安卓用 Toolbar 返回箭头（44dp 触摸目标）',
  }),
  meta('presets.editor.saveButton', '保存', 'button', EDITOR_SAVE_BOX, {
    zIndex: 6,
    props: ['disabled'],
    notes: '校验通过后写入 presetRepo；安卓用 MaterialButton（主色、minHeight 44dp）',
  }),
  meta('presets.editor.error', '编辑校验错误提示', 'text', EDITOR_ERROR_BOX, {
    zIndex: 30,
    notes: '保存失败 / 名称为空的可见错误提示；安卓用 TextInputLayout.setError 或 Snackbar',
  }),
  meta('presets.editor.notice', '世界书导入提示', 'text', EDITOR_NOTICE_BOX, { zIndex: 30 }),
  meta('presets.editor.form', '角色卡表单滚动区', 'container', FORM_SCROLL_BOX, {
    zIndex: 4,
    notes: '可滚动表单容器（内容高于视口时滚动）；安卓用 NestedScrollView + ConstraintLayout',
  }),
  meta('presets.editor.nameLabel', '名称标签', 'text', FIELD_NAME_LABEL_BOX, {
    zIndex: 5,
    notes: '字段标签；安卓用 TextInputLayout 的 hint',
  }),
  meta('presets.editor.nameInput', '名称', 'input', FIELD_NAME_BOX, {
    zIndex: 5,
    props: ['value', 'placeholder'],
    notes: '角色卡名称（必填）；安卓用 TextInputEditText（inputType=text）',
  }),
  meta('presets.editor.avatarLabel', '头像标签', 'text', FIELD_AVATAR_LABEL_BOX, {
    zIndex: 5,
    notes: '字段标签；安卓用 TextInputLayout 的 hint',
  }),
  meta('presets.editor.avatarInput', '头像', 'input', { x: 86, y: 196, width: 198, height: 40 }, {
    zIndex: 5,
    props: ['value', 'placeholder'],
    notes: 'emoji 或图片地址；安卓用 TextInputEditText（inputType=textUri）',
  }),
  meta('presets.editor.modelConfig', '模型配置', 'button', MODEL_CONFIG_BUTTON_BOX, { zIndex: 6 }),
  ...Object.values(intimacyControlMeta()).map(input => ({ ...input, layout: canvas(input.layout) })),
  meta('presets.editor.tagsLabel', '标签标签', 'text', FIELD_TAGS_LABEL_BOX, {
    zIndex: 5,
    notes: '字段标签；安卓用 TextInputLayout 的 hint',
  }),
  meta('presets.editor.tagsInput', '标签', 'input', FIELD_TAGS_BOX, {
    zIndex: 5,
    props: ['value', 'placeholder'],
    notes: '逗号 / 顿号分隔的标签；安卓用 TextInputEditText + ChipGroup 回显',
  }),
  meta('presets.editor.descriptionLabel', '简介标签', 'text', FIELD_DESC_LABEL_BOX, {
    zIndex: 5,
    notes: '字段标签；安卓用 TextInputLayout 的 hint',
  }),
  meta('presets.editor.descriptionInput', '简介', 'input', FIELD_DESC_BOX, {
    zIndex: 5,
    props: ['value', 'placeholder'],
    notes: '角色简介（多行 80dp）；安卓用 TextInputEditText（textMultiLine、gravity=top）',
  }),
  meta('presets.editor.systemPromptLabel', '系统提示词标签', 'text', FIELD_PROMPT_LABEL_BOX, {
    zIndex: 5,
    notes: '字段标签；安卓用 TextInputLayout 的 hint',
  }),
  meta('presets.editor.systemPromptInput', '系统提示词', 'input', FIELD_PROMPT_BOX, {
    zIndex: 5,
    props: ['value', 'placeholder'],
    notes: '角色设定正文（对话时作为 system 提示）；安卓用 TextInputEditText（textMultiLine、150dp）',
  }),
  meta('presets.editor.worldBookTitle', '世界书区块标题', 'text', WORLD_BOOK_TITLE_BOX, {
    zIndex: 5,
    notes: '世界书区块标题；安卓用 TextView（TextAppearance.Title）',
  }),
  meta('presets.editor.addEntryButton', '添加世界书条目', 'button', ADD_ENTRY_BUTTON_BOX, {
    zIndex: 6,
    props: ['pressed'],
    notes: '在表单末尾追加一条世界书条目；安卓用 MaterialButton（outlined 样式）',
  }),
  meta('presets.editor.importWorldBookButton', '导入世界书到角色卡', 'button', IMPORT_WORLD_BOOK_EDITOR_BOX, { zIndex: 6 }),
  meta('presets.editor.entry.card', '世界书条目卡片（模板）', 'container', ENTRY_TEMPLATE.card, {
    zIndex: 4,
    props: ['enabled', 'priority'],
    notes:
      '世界书条目模板；运行时按序号登记为 presets.editor.entry.<index>.card。属滚动内容，声明锚点按未滚动位置给出（卡片高 224dp，越过画布下沿的部分随表单滚动可见，与设置页备份卡片同约定）；安卓用 MaterialCardView',
  }),
  meta('presets.editor.entry.keywordsLabel', '关键词标签', 'text', ENTRY_TEMPLATE.keywordsLabel, {
    zIndex: 5,
    notes: '字段标签；安卓用 TextInputLayout 的 hint',
  }),
  meta('presets.editor.entry.keywordsInput', '关键词输入', 'input', ENTRY_TEMPLATE.keywordsInput, {
    zIndex: 5,
    props: ['value', 'placeholder'],
    notes: '触发注入的关键词（逗号分隔）；安卓用 TextInputEditText + ChipGroup',
  }),
  meta('presets.editor.entry.constantSwitch', '常驻条目开关', 'button', ENTRY_TEMPLATE.constant, { zIndex: 5, props: ['checked'] }),
  meta('presets.editor.entry.rules', '条目附加规则', 'text', ENTRY_TEMPLATE.rules, { zIndex: 5 }),
  meta('presets.editor.entry.contentLabel', '内容标签', 'text', ENTRY_TEMPLATE.contentLabel, {
    zIndex: 5,
    notes: '字段标签；安卓用 TextInputLayout 的 hint',
  }),
  meta('presets.editor.entry.contentInput', '内容输入', 'input', ENTRY_TEMPLATE.contentInput, {
    zIndex: 5,
    props: ['value', 'placeholder'],
    notes: '命中关键词后注入的内容（64dp 多行）；安卓用 TextInputEditText（textMultiLine）',
  }),
  meta('presets.editor.entry.enabledSwitch', '条目启用开关', 'button', ENTRY_TEMPLATE.enabled, {
    zIndex: 5,
    props: ['checked'],
    notes: '启用 / 停用该条；安卓用 SwitchMaterial（或 MaterialSwitch，minHeight 44dp）',
  }),
  meta('presets.editor.entry.priorityLabel', '优先级标签', 'text', ENTRY_TEMPLATE.priorityLabel, {
    zIndex: 5,
    notes: '字段标签；安卓用 TextInputLayout 的 hint',
  }),
  meta('presets.editor.entry.priorityInput', '优先级输入', 'input', ENTRY_TEMPLATE.priorityInput, {
    zIndex: 5,
    props: ['value'],
    notes: '数值越大越先注入；安卓用 TextInputEditText（inputType=number）',
  }),
  meta('presets.editor.entry.removeButton', '删除世界书条目', 'button', ENTRY_TEMPLATE.remove, {
    zIndex: 5,
    props: ['pressed'],
    notes: '删除该条世界书；安卓用 MaterialButton（危险色）',
  }),
];

/** 本页全部静态部件元数据（列表页 + 编辑页）。 */
export const PRESET_COMPONENTS: ComponentMetaInput[] = [
  ...LIST_COMPONENTS,
  ...EDITOR_COMPONENTS,
  ...Object.values(ROLE_MODEL_META).map((input) => ({ ...input, layout: canvas(input.layout) })),
  ...Object.values(automationEditorMeta()).map((input) => ({ ...input, layout: canvas(input.layout) })),
  ...Object.values(roleVoiceAutoDesignMeta()).map((input) => ({ ...input, layout: canvas(input.layout) })),
];

/** 供测试断言默认坐标的完整 ComponentMeta 列表。 */
export function presetsComponentMetaList(): ComponentMeta[] {
  return PRESET_COMPONENTS.map((input) => ({
    id: input.id,
    page: input.page,
    role: input.role ?? 'container',
    label: input.label,
    anchor: { frameWidth: 390, frameHeight: 844, ...input.layout },
    zIndex: input.zIndex ?? 0,
    ...(input.props === undefined ? {} : { props: [...input.props] }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  }));
}

const META_BY_ID = new Map(PRESET_COMPONENTS.map((entry) => [entry.id, entry]));

/** 按 id 读取本页静态部件元数据（供测试与后续接线复用）。 */
export function presetMetaById(id: string): ComponentMetaInput {
  const found = META_BY_ID.get(id);
  if (found === undefined) throw new Error(`[presets] 未知部件 id：${id}`);
  return found;
}

/**
 * 按 id 读取页面局部坐标（视图内联定位的唯一来源）。
 *
 * `componentMeta.ts` 的声明坐标是 390x844 画布绝对坐标（已含 `CONTENT_TOP`），
 * 而 `Framed.tsx` 渲染时接收页面局部坐标，因此这里减去同一份 `CONTENT_TOP`，
 * 保证「视图看到的坐标」与「清单登记的坐标」同源、不会漂移。
 */
export function presetLocalLayout(id: string): BoxLayout {
  const { layout } = presetMetaById(id);
  return { ...layout, y: layout.y - CONTENT_TOP };
}
